<template>
  <div class="container-custom moo-sheet-page moo-vouchers-page" :style="styleObject">
    <div class="content-custom">
      <div class="moo-sheet-shell">
        <MooSheetHead
          icon="confirmation_number"
          :title="$t('myVouchers.title')"
          @close="goBack()"
        />

        <div v-if="isLoadDone">
          <!-- Member-only, same guard the catalogue uses. -->
          <div class="mv-guest" v-if="!isLogined">
            <span class="material-icons-outlined">confirmation_number</span>
            <p>
              <span class="mv-link" @click="gotoSignIn()">{{
                $t('rewardCatalogue.logIn')
              }}</span>
              {{ $t('rewardCatalogue.orWord') }}
              <span class="mv-link" @click="gotoSignUp()">{{
                $t('rewardCatalogue.signUp')
              }}</span>
            </p>
          </div>

          <template v-else>
            <div class="mv-tabs">
              <button
                v-for="tab in tabs"
                :key="tab.type"
                type="button"
                class="mv-tab"
                :class="{ 'mv-tab--active': typeSelected === tab.type }"
                @click="changeType(tab.type)"
              >
                <span>{{ tab.label }}</span>
              </button>
            </div>

            <SearchInput
              class="mv-search"
              :placeholder="$t('myVouchers.searchPlaceholder')"
              :onChangesearchKeyword="onChangesearchKeyword"
            />

            <div class="mv-list" v-if="vouchers.length">
              <div
                v-for="(item, index) in vouchers"
                :key="index"
                class="mv-card"
                :class="{ 'mv-card--spent': isSpentTab }"
                @click="openItem(item)"
              >
                <div class="mv-card__icon">
                  <img v-if="item.icon_url" :src="item.icon_url" :alt="item.reward_title" />
                  <span class="material-icons-outlined" v-else>redeem</span>
                </div>

                <div class="mv-card__body">
                  <!-- Status sits on the name's line, not under the dates: as a
                       fourth stacked line it read as one more meta row and was
                       the easiest thing on the card to miss. -->
                  <div class="mv-card__top">
                    <div class="mv-card__name">{{ item.reward_title || "" }}</div>
                    <span
                      class="sheet-status"
                      :class="statusOf(item).modifier"
                      v-if="statusOf(item).label"
                    >
                      {{ statusOf(item).label }}
                    </span>
                  </div>

                  <div class="mv-card__meta" v-if="item.is_bundle">
                    {{ $t('myVouchers.balance', { count: item.number_of_vouchers_unused ?? 0 }) }}
                  </div>
                  <div class="mv-card__meta" v-if="item.expiry_date_text">
                    {{ $t('myVouchers.validUntil', { date: item.expiry_date_text }) }}
                  </div>

                  <div class="mv-card__foot" v-if="item.is_bundle">
                    <span class="mv-card__cta">
                      {{ $t('myVouchers.viewBundle') }}
                      <span class="material-icons-round">chevron_right</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div class="mv-empty" v-else>
              <span class="material-icons-outlined">confirmation_number</span>
              <span>{{
                searchKeyword ? $t('myVouchers.noMatch') : $t('myVouchers.empty')
              }}</span>
            </div>
          </template>
        </div>
      </div>
    </div>

    <VoucherCodeSheet
      :detail="detail"
      :show-qr="showQr"
      @copied="onCodeCopied"
    />

    <div class="position-fixed top-1 end-1 z-index-2">
      <material-snackbar
        v-if="snackbar === 'danger' || snackbar === 'success'"
        title=""
        date=""
        :description="message"
        :icon="{ component: 'campaign', color: 'white' }"
        :color="snackbar"
        :close-handler="closeSnackbar"
      />
    </div>
    <material-loading :active="loaderActive" />
  </div>
</template>

<script>
import MaterialSnackbar from "@/components/MaterialSnackbar.vue";
import MaterialLoading from "@/components/MaterialLoading.vue";
import SearchInput from "@/components/SearchInput.vue";
import MooSheetHead from "@/components/MooSheetHead.vue";
import VoucherCodeSheet from "@/components/VoucherCodeSheet.vue";
import PromocodeService from "../services/promocode.service";
import MemberService from "../services/member.service";
import { localStorageName, gotoMemberLogin } from "@/config/utils";
import "bootstrap/dist/js/bootstrap.bundle";
import $ from "jquery";

// RewardController::myReward's `type`.
const TYPE_ACTIVE = 1;
const TYPE_SPENT = 2;

const SEARCH_DEBOUNCE_MS = 500;

export default {
  name: "MyVouchers",
  components: {
    MaterialSnackbar,
    MaterialLoading,
    SearchInput,
    MooSheetHead,
    VoucherCodeSheet,
  },
  data() {
    return {
      isLoadDone: false,
      isLogined: false,
      loaderActive: false,
      snackbar: null,
      message: "",
      pathType: "",
      branchCode: "",
      tableCode: "",
      typeSelected: TYPE_ACTIVE,
      searchKeyword: "",
      searchTimerId: null,
      // Sequence token for getList(): a tab switch or a keystroke can leave an
      // older request in flight, and it must not land on top of a newer one.
      requestId: 0,
      vouchers: [],
      // The voucher whose code sheet is open. Filled by getDetail(), not by the
      // list row: the list carries no code, terms or applicability.
      detail: {},
      showQr: true,
      TYPE_ACTIVE,
      TYPE_SPENT,
    };
  },
  created() {
    this.pathType = this.$route.params.type;
    this.branchCode = this.$store.state.branchCode;
    this.tableCode = this.$route.params.table_code;

    const memberInfo = JSON.parse(
      localStorage.getItem(localStorageName("memberInfo")) || "null"
    );
    this.isLogined = !!memberInfo;

    if (!this.isLogined) {
      this.isLoadDone = true;
      return;
    }

    this.checkMemberPermissions();
    this.getList();
  },
  beforeUnmount() {
    clearTimeout(this.searchTimerId);
  },
  computed: {
    styleObject() {
      return {
        "--color":
          this.$store.state.mainThemeColorTheme2 ||
          this.$store.state.color ||
          "#201a1a",
      };
    },
    tabs() {
      return [
        { type: TYPE_ACTIVE, label: this.$t("myVouchers.tabNew") },
        { type: TYPE_SPENT, label: this.$t("myVouchers.tabUsed") },
      ];
    },
    isSpentTab() {
      return this.typeSelected === TYPE_SPENT;
    },
  },
  methods: {
    goBack() {
      this.$router.push({
        name: "Profile",
        params: {
          merchant_code: this.$store.state.merchantCode,
          type: this.pathType,
          branch_code: this.branchCode,
          table_code: this.tableCode,
        },
      });
    },
    gotoSignIn() {
      gotoMemberLogin("profile", false, "login");
    },
    gotoSignUp() {
      gotoMemberLogin("profile", false, "signup");
    },
    // Same gate the merchant sets for the member site's My Rewards page; a
    // merchant with it switched off should not reach this screen by URL.
    checkMemberPermissions() {
      MemberService.checkMemberPermissions().then(
        (response) => {
          if (
            response.data.result.isSuccess &&
            !response.data.data.includes("My Rewards")
          ) {
            this.goBack();
          }
        },
        () => {}
      );
    },
    // One chip per row. On the active tab a bundle carries no chip: the states
    // that matter for it are its own vouchers', and those live on its detail
    // screen behind "View Bundle". On the spent tab it does need one — a bundle
    // only lands there because its own expiry has passed, and without a chip it
    // read exactly like a live one.
    statusOf(item) {
      if (item.is_bundle) {
        return this.isSpentTab
          ? {
              label: this.$t("myVouchers.statusExpired"),
              modifier: "sheet-status--expired",
            }
          : this.newChip(item);
      }
      if (item.locked) {
        return {
          label: this.$t("myVouchers.statusLocked"),
          modifier: "sheet-status--locked",
        };
      }
      if (!this.isSpentTab) {
        return this.newChip(item);
      }
      return item.use
        ? { label: this.$t("myVouchers.statusUsed"), modifier: "sheet-status--used" }
        : {
            label: this.$t("myVouchers.statusExpired"),
            modifier: "sheet-status--expired",
          };
    },
    // The rows at the top are the most recent, so this marks where "today" ends
    // and older stock begins. is_new is decided by the API against the
    // merchant's own clock, not the phone's.
    newChip(item) {
      return item.is_new
        ? { label: this.$t("myVouchers.statusNew"), modifier: "sheet-status--new" }
        : { label: "", modifier: "" };
    },
    // Newest acquisition first: what the member has just redeemed is what they
    // are looking for. The API's own order is by the merchant's template date,
    // which is identical for everyone and says nothing about this member.
    //
    // exchanged_date is when THIS member got their copy. Rows without a usable
    // one sort last in either direction — hence the explicit nulls rather than
    // a sentinel number, which would float them to whichever end the comparison
    // happens to favour.
    newestFirst(list) {
      return [...list].sort((a, b) => {
        const ta = this.acquiredAt(a);
        const tb = this.acquiredAt(b);
        if (ta === null && tb === null) {
          return 0;
        }
        if (ta === null) {
          return 1;
        }
        if (tb === null) {
          return -1;
        }
        return tb - ta;
      });
    },
    acquiredAt(item) {
      const raw = item.exchanged_date;
      if (!raw) {
        return null;
      }
      // 'YYYY-MM-DD HH:mm:ss' — Safari will not parse that with a space.
      const time = Date.parse(String(raw).replace(" ", "T"));
      return isNaN(time) ? null : time;
    },
    changeType(type) {
      if (this.typeSelected === type) {
        return;
      }
      this.typeSelected = type;
      this.getList();
    },
    onChangesearchKeyword(event) {
      this.searchKeyword = event.target.value;
      clearTimeout(this.searchTimerId);
      this.searchTimerId = setTimeout(() => {
        this.getList();
      }, SEARCH_DEBOUNCE_MS);
    },
    fetch(type) {
      return PromocodeService.getMyRewards({
        type,
        search_keyword: this.searchKeyword,
        // Owned bundles are folded into the same list, flagged is_bundle.
        with_bundle: 1,
      });
    },
    // A locked voucher cannot be redeemed today, so it belongs with the rest of
    // what the member cannot use rather than sitting among the ones they can.
    // The API returns it in the active list regardless — RewardController hard-
    // codes myReward's $isShowVoucherLocked — so the split happens here, the
    // same way VoucherBundleDetail splits a bundle's own vouchers. That costs
    // the spent tab a second request: the locked rows only exist in the active
    // list, and both requests carry the same search keyword so the two halves
    // always describe the same query.
    async getList() {
      this.showLoader();
      this.vouchers = [];

      const ticket = ++this.requestId;
      const spent = this.isSpentTab;

      try {
        const responses = await Promise.all(
          spent
            ? [this.fetch(TYPE_ACTIVE), this.fetch(TYPE_SPENT)]
            : [this.fetch(TYPE_ACTIVE)]
        );

        // Tab or keyword moved on while this was in flight.
        if (ticket !== this.requestId) {
          return;
        }

        const failed = responses.find((r) => !r.data.result.isSuccess);
        if (failed) {
          this.snackbar = "danger";
          this.message = failed.data.result.message;
          this.setTimeOutNotification();
        } else {
          const active = responses[0].data.data ?? [];
          if (spent) {
            // Locked first: they are still live and may unlock, unlike the used
            // and expired ones behind them, which are history.
            this.vouchers = [
              ...this.newestFirst(active.filter((item) => item.locked)),
              ...this.newestFirst(responses[1].data.data ?? []),
            ];
          } else {
            this.vouchers = this.newestFirst(
              active.filter((item) => !item.locked)
            );
          }
        }
      } catch (error) {
        if (ticket !== this.requestId) {
          return;
        }
        this.snackbar = "danger";
        this.message =
          error?.response?.data?.result?.message ?? "Cannot load vouchers";
        this.setTimeOutNotification();
      }

      this.isLoadDone = true;
      this.hideLoader();
    },
    openItem(item) {
      if (item.is_bundle) {
        this.$router.push({
          name: "VoucherBundleDetail",
          params: { ...this.$route.params, id: item.id },
          // Reward mode: the bundle the MEMBER owns, not the one on sale.
          // member_voucher_bundle_id picks which of several purchases of the
          // same bundle to open.
          query: {
            is_reward: 1,
            member_voucher_bundle_id: item.member_voucher_bundle_id ?? 0,
          },
        });
        return;
      }
      this.getDetail(item);
    },
    // The list row carries no code, terms or applicability — is_my_reward = 1
    // resolves the voucher THIS member holds, so the code belongs to them.
    getDetail(item) {
      this.showLoader();
      this.showQr = !this.isSpentTab && !item.locked;

      PromocodeService.getRewardDetail(item.voucher_id ?? item.id, 1).then(
        (response) => {
          if (response.data.result.isSuccess) {
            const reward = response.data.data.reward ?? {};
            // The list's expiry is the member's own copy; the detail endpoint
            // returns the voucher's generic one.
            if (item.expiry_date_text) {
              reward.expiry_date_text = item.expiry_date_text;
            }
            this.detail = reward;
            $(".btn-voucher-code-modal").click();
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
          }
          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message =
            error?.response?.data?.result?.message ?? "Cannot load voucher";
          this.setTimeOutNotification();
          this.hideLoader();
        }
      );
    },
    onCodeCopied() {
      this.snackbar = "success";
      this.message = this.$t("myVouchers.copied");
      this.setTimeOutNotification();
    },
    showLoader() {
      this.loaderActive = true;
    },
    hideLoader() {
      this.loaderActive = false;
    },
    closeSnackbar() {
      this.snackbar = null;
    },
    setTimeOutNotification() {
      setTimeout(() => {
        this.snackbar = null;
      }, 3000);
    },
  },
};
</script>

<style scoped>
/* Shell, tokens, sheet head and modal chrome come from moo-sheet.css. */
.container-custom {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.content-custom {
  flex: 1;
  overflow: auto;
}

/* ---- tabs ---- */
.mv-tabs {
  display: flex;
  background: #ffffff;
  border: 1px solid var(--sheet-border);
  border-radius: 12px;
  padding: 3px;
  margin-bottom: 12px;
}
.mv-tab {
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  border-radius: 9px;
  padding: 8px 4px;
  font-size: 13px;
  font-weight: 600;
  color: #857372;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
/* The label needs its own box: text-overflow does not apply to the anonymous
   text of a flex item, so a long translation would be cut mid-word instead. */
.mv-tab span {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mv-tab--active {
  background: var(--sheet);
  color: #fff;
}

/* ---- search ---- */
.mv-search {
  display: flex;
  align-items: center;
  background: #ffffff;
  border: 1px solid var(--sheet-border);
  border-radius: 12px;
  margin-bottom: 16px !important;
}
/* SearchInput is an underline field; the box takes over the focus affordance. */
.mv-search :deep(input.nosubmit) {
  border-bottom: none;
  padding: 11px 12px 11px 40px;
  border-radius: 12px;
}
.mv-search :deep(input[type="search"]:focus) {
  border: none !important;
  border-bottom: none !important;
}
.mv-search:focus-within {
  border-color: var(--sheet);
}

/* ---- list ---- */
.mv-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-bottom: var(--sheet-pad-b);
}
.mv-card {
  display: flex;
  gap: 12px;
  background: #ffffff;
  border: 1px solid var(--sheet-border);
  border-radius: 16px;
  padding: 12px;
  cursor: pointer;
  transition: transform 0.1s ease, border-color 0.15s ease;
}
.mv-card:active {
  transform: scale(0.995);
}
/* Used and expired vouchers read as inactive through the tinted card and the
   chip. The TEXT keeps full contrast — dimming it was doing the opposite of
   what a spent voucher needs, which is to still be readable when the member is
   checking what they already used. */
.mv-card--spent {
  background: var(--sheet-bg);
}
.mv-card__icon {
  width: 62px;
  height: 62px;
  flex-shrink: 0;
  border-radius: 12px;
  background: var(--sheet-08);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.mv-card__icon img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.mv-card__icon .material-icons-outlined {
  font-size: 26px;
  color: var(--sheet);
}
.mv-card__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.mv-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}
.mv-card__name {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 700;
  color: #241a12;
  overflow-wrap: anywhere;
}
.mv-card__meta {
  font-size: 12px;
  color: #857372;
}
.mv-card__foot {
  display: flex;
  justify-content: flex-end;
  margin-top: 6px;
}
.mv-card__cta {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-left: auto;
  font-size: 12px;
  font-weight: 700;
  color: var(--sheet);
  white-space: nowrap;
}
/* 18px, not the inherited 12px: Material glyphs fill their em box and only
   land on whole pixels at even sizes. */
.mv-card__cta .material-icons-round {
  font-size: 18px;
}

/* ---- empty / guest ---- */
.mv-empty,
.mv-guest {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 44px 16px;
  color: #857372;
  font-size: 13px;
  text-align: center;
}
.mv-empty .material-icons-outlined,
.mv-guest .material-icons-outlined {
  font-size: 34px;
  color: var(--sheet-border);
}
.mv-link {
  color: var(--sheet);
  font-weight: 700;
  cursor: pointer;
  text-decoration: underline;
}

</style>
